// Быстрая задача: микро-форма под контуром новой задачи прямо на сетке — название и исполнители, Enter создаёт
// задачу длиной в неделю на месте контура, растянуть и подвинуть — мышью. Ctrl/Cmd+Enter — полная карточка
// с уже набранным. Esc и клик вне — отмена.
import { useCallback, useRef, useState, type KeyboardEvent } from 'react';
import { useDismiss } from '@core/ui/dismiss';
import { barGeometry, type Grid } from './geometry';
import { GhostBar } from './TaskBar';
import { fieldInput } from '@core/ui/form';
import { Button } from '@core/ui/Button';
import { AssigneePicker, usePeopleList, useTeamList } from './AssigneePicker';
import { EntitySelect, type EntityOption } from '@core/ui/EntitySelect';

export const QUICK_FORM_WIDTH = 460;

/** Что экран делает с набранным в микро-форме; H — контур (даты и строка), у которого она стоит. */
export type QuickHandlers<H> = { hint: H; onCreate: (title: string, assignees: string[]) => void; onFull: (title: string, assignees: string[]) => void; onCancel: () => void };

type FormOpts = { defaultAssignees?: string[]; project?: Props['project']; assignees?: boolean; duration?: string };
type Props = {
  left: number; top: number;
  defaultAssignees?: string[];
  /** Где проект не задан строкой (People) — выбор проекта в форме. */
  project?: { value: string; options: EntityOption[]; onChange: (id: string) => void };
  /** Лонч: у задач нет исполнителей — только название. */
  assignees?: boolean;
  /** Какой длины получится задача — для подсказки под формой. */
  duration?: string;
  onCreate: (title: string, assignees: string[]) => void;
  onFull: (title: string, assignees: string[]) => void;
  onCancel: () => void;
};

export function QuickTaskForm({ left, top, defaultAssignees = [], project, assignees: withAssignees = true, duration = 'one-week', onCreate, onFull, onCancel }: Props) {
  const [title, setTitle] = useState('');
  const [assignees, setAssignees] = useState<string[]>(defaultAssignees);
  const people = usePeopleList();
  const teams = useTeamList();
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(ref, true, onCancel);
  const submit = useCallback(() => { if (title.trim()) onCreate(title.trim(), assignees); }, [title, assignees, onCreate]);
  // Enter создаёт из любого места формы (набрали название, мышью выбрали людей — Enter должен сработать);
  // список исполнителей свой Enter уже потребил (defaultPrevented) — выбор имени задачу не создаёт
  const onKey = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); e.stopPropagation(); onFull(title.trim(), assignees); return; }
    if (e.key === 'Enter' && !e.defaultPrevented) { e.preventDefault(); submit(); return; }
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onCancel(); }
  };
  return (
    <div
      ref={ref} data-quick-task role="dialog" aria-label="Quick task"
      className="absolute z-[20] bg-white border border-gray-300 rounded-md shadow-xl p-2 flex flex-col gap-2 text-sm"
      style={{ left, top, width: QUICK_FORM_WIDTH }}
      // сетка под формой ловит pointerdown для рамки и перетаскивания — клики по форме ей не отдаём
      onPointerDown={(e) => e.stopPropagation()} onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }} onKeyDown={onKey}
    >
      <div className="flex items-center gap-2">
        <input
          className={`${fieldInput} py-1.5`} value={title} placeholder="Task title" aria-label="Task title" autoFocus data-quick-title
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      {project && <EntitySelect label="Project" value={project.value} options={project.options} onChange={(id) => { if (id) project.onChange(id); }} />}
      {withAssignees && <AssigneePicker value={assignees} onChange={setAssignees} people={people} teams={teams} onEnterEmpty={submit} />}
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={submit} disabled={!title.trim()} data-quick-create>Create</Button>
        <p className="text-xs text-gray-500 px-0.5 flex-1"><b>Enter</b> — create a {duration} task here · <b>Ctrl+Enter</b> — full card · <b>Esc</b> — cancel</p>
      </div>
    </div>
  );
}

/**
 * Контур новой задачи там, куда она ляжет (пока открыто меню пустого места), и микро-форма под ним, когда выбрали
 * «Quick task here». place — верх и высота контура в строке: у каждого экрана свои треки.
 */
export function NewTaskGhost<H extends { start_date: string; end_date: string }>({ hint, quick, grid, place, form }: {
  hint: H | null; quick: QuickHandlers<H> | null; grid: Grid; place: (target: H) => { top: number; height: number }; form?: FormOpts;
}) {
  const target = hint ?? quick?.hint;
  if (!target) return null;
  const g = barGeometry(target, grid);
  if (!g) return null;
  const { top, height } = place(target);
  return (
    <>
      <GhostBar left={g.left} width={g.width} top={top} height={height}>New task</GhostBar>
      {quick && !hint && <QuickTaskForm left={Math.max(0, Math.min(g.left, grid.width - QUICK_FORM_WIDTH))} top={top + height + 4} {...form} onCreate={quick.onCreate} onFull={quick.onFull} onCancel={quick.onCancel} />}
    </>
  );
}
